import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useMusicKit } from './MusicKitContext';
import { useSession } from './SessionContext';

const PlaylistMusicContext = createContext(null);

// Local storage key for persisting playlist state
const PLAYLIST_STATE_KEY = 'tapInPlaylistMusicState';

export const usePlaylistMusic = () => {
  const context = useContext(PlaylistMusicContext);
  if (!context) {
    throw new Error('usePlaylistMusic must be used within a PlaylistMusicProvider');
  }
  return context;
};

// Helper to safely get from localStorage
const getPersistedState = () => {
  try {
    const saved = localStorage.getItem(PLAYLIST_STATE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      console.log('[PlaylistMusic] Loaded persisted state:', parsed);
      return parsed;
    }
  } catch (e) {
    console.error('[PlaylistMusic] Error loading persisted state:', e);
  }
  return null;
};

// Helper to safely save to localStorage
const persistState = (state) => {
  try {
    localStorage.setItem(PLAYLIST_STATE_KEY, JSON.stringify(state));
    console.log('[PlaylistMusic] Persisted state:', state);
  } catch (e) {
    console.error('[PlaylistMusic] Error persisting state:', e);
  }
};

export const PlaylistMusicProvider = ({ children }) => {
  // Initialize state from localStorage if available
  const initialState = getPersistedState();
  
  // Current playlist being played
  const [currentPlaylist, setCurrentPlaylist] = useState(initialState?.currentPlaylist || null);
  const [currentPlaylistId, setCurrentPlaylistId] = useState(initialState?.currentPlaylistId || null);
  const [playlistQueue, setPlaylistQueue] = useState(initialState?.playlistQueue || []);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(initialState?.currentTrackIndex || 0);
  const [isPlaylistPlaying, setIsPlaylistPlaying] = useState(false); // Always start paused on reload
  
  // CRITICAL: Use refs to avoid stale closures in the track ended callback
  const currentTrackIndexRef = useRef(currentTrackIndex);
  const playlistQueueRef = useRef(playlistQueue);
  const isPlaylistPlayingRef = useRef(isPlaylistPlaying);
  
  // Keep refs in sync with state
  useEffect(() => {
    currentTrackIndexRef.current = currentTrackIndex;
  }, [currentTrackIndex]);
  
  useEffect(() => {
    playlistQueueRef.current = playlistQueue;
  }, [playlistQueue]);
  
  useEffect(() => {
    isPlaylistPlayingRef.current = isPlaylistPlaying;
  }, [isPlaylistPlaying]);
  
  // Get session context to check if session is active
  const { isInActiveSession, activeSessionData } = useSession();
  
  // Get MusicKit for actual playback
  const { 
    isConfigured: musicKitConfigured,
    isAuthorized: musicKitAuthorized,
    isPlaying: musicKitIsPlaying,
    currentSong: musicKitCurrentSong,
    playSong,
    playQueue,  // Use MusicKit's native queue for auto-play
    play,
    pause,
    setMusicSource, // Track music source
  } = useMusicKit();

  // Refs to track state changes
  const prevSessionActive = useRef(isInActiveSession);
  const wasPlayingRef = useRef(false);

  // Persist state whenever it changes
  useEffect(() => {
    if (currentPlaylist || currentPlaylistId || playlistQueue.length > 0) {
      persistState({
        currentPlaylist,
        currentPlaylistId,
        playlistQueue,
        currentTrackIndex
      });
    }
  }, [currentPlaylist, currentPlaylistId, playlistQueue, currentTrackIndex]);

  // Handle session priority - pause playlist when session starts
  useEffect(() => {
    // Session just became active
    if (isInActiveSession && !prevSessionActive.current) {
      if (isPlaylistPlaying) {
        console.log('[PlaylistMusic] Session started - pausing playlist');
        wasPlayingRef.current = true;
        // Use timeout to avoid setState in effect body warning
        setTimeout(() => {
          setIsPlaylistPlaying(false);
          if (pause) pause();
        }, 0);
      }
    }
    
    // Session just ended
    if (!isInActiveSession && prevSessionActive.current) {
      if (wasPlayingRef.current && currentPlaylist) {
        console.log('[PlaylistMusic] Session ended - resuming playlist');
        wasPlayingRef.current = false;
        // Resume playback
        setTimeout(() => {
          setIsPlaylistPlaying(true);
          const currentTrack = playlistQueue[currentTrackIndex];
          if (currentTrack && playSong) {
            playSong(currentTrack);
          }
        }, 0);
      }
    }
    
    prevSessionActive.current = isInActiveSession;
  }, [isInActiveSession]);

  // Get current track
  const getCurrentTrack = useCallback(() => {
    if (playlistQueue.length === 0) return null;
    return playlistQueue[currentTrackIndex] || null;
  }, [playlistQueue, currentTrackIndex]);

  // Sync currentTrackIndex with MusicKit's current playing song
  // This keeps UI in sync when MusicKit auto-advances through the queue
  useEffect(() => {
    if (!musicKitCurrentSong || playlistQueue.length === 0) return;
    
    const currentSongId = musicKitCurrentSong.id;
    const newIndex = playlistQueue.findIndex(song => 
      (song.id === currentSongId) || (song.song_id === currentSongId)
    );
    
    if (newIndex !== -1 && newIndex !== currentTrackIndex) {
      console.log('[PlaylistMusic] MusicKit advanced to song index:', newIndex, 'Song:', musicKitCurrentSong.name);
      setCurrentTrackIndex(newIndex);
      currentTrackIndexRef.current = newIndex;
    }
  }, [musicKitCurrentSong, playlistQueue, currentTrackIndex]);

  // Play a playlist - uses MusicKit's native queue for seamless auto-play
  const playPlaylist = useCallback(async (playlist, startIndex = 0) => {
    console.log('[PlaylistMusic] ========== PLAY PLAYLIST CALLED ==========');
    console.log('[PlaylistMusic] Playlist:', playlist?.name || playlist?.title);
    console.log('[PlaylistMusic] Songs count:', playlist?.songs?.length);
    console.log('[PlaylistMusic] Start index:', startIndex);
    console.log('[PlaylistMusic] playQueue available:', !!playQueue);
    console.log('[PlaylistMusic] playSong available:', !!playSong);
    console.log('[PlaylistMusic] isInActiveSession:', isInActiveSession);
    console.log('[PlaylistMusic] activeSessionData:', !!activeSessionData);
    
    if (!playlist || !playlist.songs || playlist.songs.length === 0) {
      console.warn('[PlaylistMusic] Cannot play empty playlist');
      return;
    }

    // Only block if there's a REAL active session (both session data AND active flag)
    if (isInActiveSession && activeSessionData?.id) {
      console.log('[PlaylistMusic] Real session active - cannot start playlist');
      return;
    }

    console.log('[PlaylistMusic] Playing playlist:', playlist.name || playlist.title);
    console.log('[PlaylistMusic] Total songs:', playlist.songs.length, 'Starting at index:', startIndex);
    
    // Update refs FIRST (before state) to ensure callback has correct values
    playlistQueueRef.current = playlist.songs;
    currentTrackIndexRef.current = startIndex;
    isPlaylistPlayingRef.current = true;
    
    // Then update state
    setCurrentPlaylist(playlist);
    setCurrentPlaylistId(playlist.id);
    setPlaylistQueue(playlist.songs);
    setCurrentTrackIndex(startIndex);
    setIsPlaylistPlaying(true);
    
    // CRITICAL: Set music source to 'playlist' so AppLayout shows PlaylistSoundBar
    if (setMusicSource) {
      setMusicSource('playlist');
    }

    // Extract all song IDs from the playlist (starting from startIndex) IN ORDER
    const songsToPlay = playlist.songs.slice(startIndex);
    const songIds = songsToPlay.map(song => song.id || song.song_id).filter(Boolean);
    
    console.log('[PlaylistMusic] Song IDs to play:', songIds);

    // Use MusicKit's native queue - this handles auto-play automatically
    if (playQueue && songIds.length > 0) {
      try {
        console.log('[PlaylistMusic] Calling playQueue with', songIds.length, 'songs');
        const result = await playQueue(songIds);
        console.log('[PlaylistMusic] playQueue result:', result);
      } catch (error) {
        console.error('[PlaylistMusic] Error setting up queue:', error);
        // Fallback: try playing just the first song
        if (playSong && songIds[0]) {
          console.log('[PlaylistMusic] Fallback: playing first song only');
          await playSong(songIds[0]);
        }
      }
    } else if (playSong && songIds[0]) {
      // Fallback if playQueue not available
      console.log('[PlaylistMusic] playQueue not available, using playSong fallback');
      await playSong(songIds[0]);
    } else {
      console.error('[PlaylistMusic] No playQueue or playSong available!');
    }
  }, [isInActiveSession, activeSessionData, playQueue, playSong]);

  // Play a specific song from the current playlist - sets up queue from that point
  const playSongAtIndex = useCallback(async (index) => {
    if (index < 0 || index >= playlistQueue.length) return;
    
    // Only block if there's a REAL active session
    if (isInActiveSession && activeSessionData?.id) {
      console.log('[PlaylistMusic] Real session active - cannot play playlist song');
      return;
    }

    console.log('[PlaylistMusic] Playing from index:', index, 'of', playlistQueue.length);
    
    setCurrentTrackIndex(index);
    currentTrackIndexRef.current = index;
    setIsPlaylistPlaying(true);
    isPlaylistPlayingRef.current = true;
    
    // Set up queue from this index onwards for seamless auto-play
    const songsFromIndex = playlistQueue.slice(index);
    const songIds = songsFromIndex.map(song => song.id || song.song_id).filter(Boolean);
    
    if (playQueue && songIds.length > 0) {
      try {
        console.log('[PlaylistMusic] Setting queue from index', index, 'with', songIds.length, 'songs');
        await playQueue(songIds);
      } catch (error) {
        console.error('[PlaylistMusic] Error setting queue:', error);
        // Fallback to single song
        if (playSong && songIds[0]) {
          await playSong(songIds[0]);
        }
      }
    } else if (playSong && songIds[0]) {
      await playSong(songIds[0]);
    }
  }, [playlistQueue, isInActiveSession, activeSessionData, playQueue, playSong]);

  // Toggle play/pause
  const togglePlayPause = useCallback(async () => {
    // Only block if there's a REAL active session
    if (isInActiveSession && activeSessionData?.id) {
      console.log('[PlaylistMusic] Real session active - cannot toggle playlist');
      return;
    }

    console.log('[PlaylistMusic] togglePlayPause called, isPlaylistPlaying:', isPlaylistPlaying);

    if (isPlaylistPlaying) {
      // Pause playback
      setIsPlaylistPlaying(false);
      if (pause) {
        try {
          await pause();
          console.log('[PlaylistMusic] Paused successfully');
        } catch (error) {
          console.error('[PlaylistMusic] Error pausing:', error);
        }
      }
    } else {
      // Resume playback - use play() which handles resume
      setIsPlaylistPlaying(true);
      if (play) {
        try {
          await play();
          console.log('[PlaylistMusic] Resumed successfully');
        } catch (error) {
          console.error('[PlaylistMusic] Error resuming:', error);
          // If resume fails, try playing the current track from scratch
          const currentTrack = getCurrentTrack();
          if (currentTrack && playSong) {
            console.log('[PlaylistMusic] Fallback: playing current track');
            await playSong(currentTrack);
          }
        }
      }
    }
  }, [isInActiveSession, isPlaylistPlaying, getCurrentTrack, pause, play, playSong]);

  // Skip to next track - uses playQueue to maintain auto-play queue
  // IMPORTANT: Always starts playback immediately after skip
  const skipNext = useCallback(async () => {
    if (isInActiveSession || playlistQueue.length === 0) return;

    const nextIndex = currentTrackIndex + 1;
    if (nextIndex >= playlistQueue.length) {
      // End of playlist - stop or loop
      console.log('[PlaylistMusic] End of playlist');
      setIsPlaylistPlaying(false);
      isPlaylistPlayingRef.current = false;
      setCurrentTrackIndex(0);
      currentTrackIndexRef.current = 0;
      return;
    }

    console.log('[PlaylistMusic] Skipping to next track, index:', nextIndex);
    setCurrentTrackIndex(nextIndex);
    currentTrackIndexRef.current = nextIndex;
    setIsPlaylistPlaying(true);
    isPlaylistPlayingRef.current = true;
    
    // Use playQueue to set up remaining songs for auto-play
    const songsFromIndex = playlistQueue.slice(nextIndex);
    const songIds = songsFromIndex.map(song => song.id || song.song_id).filter(Boolean);
    
    if (playQueue && songIds.length > 0) {
      try {
        console.log('[PlaylistMusic] Setting queue from index', nextIndex, 'with', songIds.length, 'songs - WILL START PLAYING');
        await playQueue(songIds);
        // Ensure playback starts if it wasn't already playing
        // playQueue should auto-play, but we ensure state is correct
        console.log('[PlaylistMusic] Queue set, playback should be active');
      } catch (error) {
        console.error('[PlaylistMusic] Error playing next track:', error);
        // Fallback: try playing just the next song directly
        if (playSong && songIds[0]) {
          console.log('[PlaylistMusic] Fallback: playing next song directly');
          await playSong(songIds[0]);
        }
      }
    } else if (playSong && playlistQueue[nextIndex]) {
      // Fallback if playQueue not available
      const nextSong = playlistQueue[nextIndex];
      const songId = nextSong.id || nextSong.song_id;
      if (songId) {
        console.log('[PlaylistMusic] playQueue not available, using playSong fallback');
        await playSong(songId);
      }
    }
  }, [isInActiveSession, playlistQueue, currentTrackIndex, playQueue, playSong]);

  // Skip to previous track - uses playQueue to maintain auto-play queue
  // IMPORTANT: Always starts playback immediately after skip
  const skipPrevious = useCallback(async () => {
    if (isInActiveSession || playlistQueue.length === 0) return;

    const prevIndex = Math.max(0, currentTrackIndex - 1);
    console.log('[PlaylistMusic] Skipping to previous track, index:', prevIndex);
    setCurrentTrackIndex(prevIndex);
    currentTrackIndexRef.current = prevIndex;
    setIsPlaylistPlaying(true);
    isPlaylistPlayingRef.current = true;
    
    // Use playQueue to set up remaining songs for auto-play
    const songsFromIndex = playlistQueue.slice(prevIndex);
    const songIds = songsFromIndex.map(song => song.id || song.song_id).filter(Boolean);
    
    if (playQueue && songIds.length > 0) {
      try {
        console.log('[PlaylistMusic] Setting queue from index', prevIndex, 'with', songIds.length, 'songs - WILL START PLAYING');
        await playQueue(songIds);
        // Ensure playback starts if it wasn't already playing
        console.log('[PlaylistMusic] Queue set, playback should be active');
      } catch (error) {
        console.error('[PlaylistMusic] Error playing previous track:', error);
        // Fallback: try playing just the previous song directly
        if (playSong && songIds[0]) {
          console.log('[PlaylistMusic] Fallback: playing previous song directly');
          await playSong(songIds[0]);
        }
      }
    } else if (playSong && playlistQueue[prevIndex]) {
      // Fallback if playQueue not available
      const prevSong = playlistQueue[prevIndex];
      const songId = prevSong.id || prevSong.song_id;
      if (songId) {
        console.log('[PlaylistMusic] playQueue not available, using playSong fallback');
        await playSong(songId);
      }
    }
  }, [isInActiveSession, playlistQueue, currentTrackIndex, playQueue, playSong]);

  // Stop playlist playback and clear state
  const stopPlaylist = useCallback(() => {
    setIsPlaylistPlaying(false);
    isPlaylistPlayingRef.current = false;
    setCurrentPlaylist(null);
    setCurrentPlaylistId(null);
    setPlaylistQueue([]);
    playlistQueueRef.current = [];
    setCurrentTrackIndex(0);
    currentTrackIndexRef.current = 0;
    setCurrentTrackIndex(0);
    if (pause) pause();
    // Clear music source since playlist is stopped
    if (setMusicSource) {
      setMusicSource(null);
    }
    // Clear persisted state
    localStorage.removeItem(PLAYLIST_STATE_KEY);
  }, [pause, setMusicSource]);

  // Add a song to the current playlist queue
  const addSongToPlaylist = useCallback(async (song) => {
    if (!song) return;
    
    // Check if song already exists
    if (playlistQueue.some(s => s.id === song.id)) {
      console.log('[PlaylistMusic] Song already in queue');
      return;
    }
    
    console.log('[PlaylistMusic] Adding song to queue at position:', playlistQueue.length);
    
    // Add position/order to the song
    const songWithOrder = {
      ...song,
      position: playlistQueue.length,
      added_at: new Date().toISOString()
    };
    
    setPlaylistQueue(prev => [...prev, songWithOrder]);
    
    // Also update the currentPlaylist if it exists
    if (currentPlaylist) {
      setCurrentPlaylist(prev => ({
        ...prev,
        songs: [...(prev.songs || []), songWithOrder]
      }));
      
      // Persist to backend if we have a playlist ID
      if (currentPlaylist.id) {
        try {
          const API_URL = process.env.REACT_APP_BACKEND_URL;
          // Map frontend song fields to backend SongAdd model
          const songPayload = {
            song_id: song.id,
            title: song.title || song.name || '',
            artist: song.artist || song.artistName || '',
            artwork: song.artwork_url || song.artwork || song.artworkUrl || null,
            duration_ms: song.duration_ms || song.durationInMillis || null,
            preview_url: song.preview_url || song.previewUrl || null
          };
          await fetch(`${API_URL}/api/playlists/${currentPlaylist.id}/songs`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('tapInToken')}`
            },
            body: JSON.stringify(songPayload)
          });
          console.log('[PlaylistMusic] Song persisted to backend');
        } catch (err) {
          console.error('[PlaylistMusic] Error persisting song:', err);
        }
      }
    }
  }, [playlistQueue, currentPlaylist]);

  // Fire state for playlist
  const [fireCount, setFireCount] = useState(0);
  const [hasUserFired, setHasUserFired] = useState(false);
  const [firedSongIds, setFiredSongIds] = useState(new Set());

  // Load fire count when playlist changes
  useEffect(() => {
    if (currentPlaylistId) {
      // Reset fire state for new playlist
      setFireCount(currentPlaylist?.fire_count || 0);
      setHasUserFired(currentPlaylist?.has_fired || false);
      setFiredSongIds(new Set());
    }
  }, [currentPlaylistId, currentPlaylist?.fire_count, currentPlaylist?.has_fired]);

  // Send fire reaction for a song in playlist
  const sendFireReaction = useCallback(async (songId) => {
    if (!currentPlaylistId) return;
    
    // Check if already fired for this song
    if (songId && firedSongIds.has(songId)) {
      console.log('[PlaylistMusic] Already fired for this song');
      return;
    }

    console.log('[PlaylistMusic] Sending fire reaction for playlist:', currentPlaylistId);
    
    // Mark as fired locally immediately for responsiveness
    if (songId) {
      setFiredSongIds(prev => new Set([...prev, songId]));
    }
    setHasUserFired(true);
    setFireCount(prev => prev + 1);
    
    // Persist to backend
    try {
      const token = localStorage.getItem('tapInToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/playlists/${currentPlaylistId}/fire`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFireCount(data.fire_count);
        console.log('[PlaylistMusic] Fire persisted, new count:', data.fire_count);
      }
    } catch (error) {
      console.error('[PlaylistMusic] Failed to persist fire:', error);
    }
  }, [currentPlaylistId, firedSongIds]);

  // NOTE: Auto-play is now handled by MusicKit's native queue (set in playQueue)
  // No callback registration needed - MusicKit automatically advances to next song
  // The sync effect above (watching musicKitCurrentSong) keeps our UI index in sync

  // Set active playlist context (for fire/fingerprint) without starting playback
  const setActivePlaylist = useCallback((playlist) => {
    if (!playlist?.id) return;
    setCurrentPlaylistId(playlist.id);
    setCurrentPlaylist(playlist);
    if (playlist.songs && playlist.songs.length > 0) {
      setPlaylistQueue(playlist.songs);
    }
  }, []);

  const value = {
    // State
    currentPlaylist,
    currentPlaylistId,
    playlistQueue,
    currentTrackIndex,
    isPlaylistPlaying,
    
    // Fire state
    fireCount,
    hasUserFired,
    sendFireReaction,
    
    // Computed - hasPlaylist is true if we have playlist data OR a queue with songs
    currentTrack: getCurrentTrack(),
    hasPlaylist: !!(currentPlaylist || (playlistQueue && playlistQueue.length > 0)),
    // Check if current user can add songs (owner or guest with permission)
    canAddSongs: currentPlaylist?.is_owner || currentPlaylist?.is_guest || currentPlaylist?.allow_guests_to_add_music,
    
    // Actions
    playPlaylist,
    playSongAtIndex,
    togglePlayPause,
    skipNext,
    skipPrevious,
    stopPlaylist,
    addSongToPlaylist,
    setActivePlaylist,
    getCurrentTrack,
  };

  return (
    <PlaylistMusicContext.Provider value={value}>
      {children}
    </PlaylistMusicContext.Provider>
  );
};

export default PlaylistMusicContext;
